// @wc-ignore-file
import type { DomainInviteReportRow, WorkspaceRole } from "@shared/types/supabase.types";

/**
 * CR-185: capacity and role rules of a domain invite, shared by the members
 * pages, the sign-up form and the accept page. Pure data logic (no copy):
 * every user-facing string lives in the components.
 *
 * `invites.multiple_use` is a smallint where 0 means unlimited; `usage_count`
 * counts the people who joined through the link (D1, D3).
 */
export const MAX_DOMAIN_INVITE_USES = 32767;

export type DomainInviteContainerType = "workspace" | "project";

/** What a domain invite grants and how many people it accepts. */
export type DomainInviteSettings = {
  role: WorkspaceRole;
  /** 0 = unlimited. */
  multipleUse: number;
};

export const DEFAULT_DOMAIN_INVITE_SETTINGS: DomainInviteSettings = {
  role: "member",
  multipleUse: 0,
};

export function isUnlimited(multipleUse: number | null | undefined): boolean {
  return !multipleUse;
}

export function isFull(
  usageCount: number | null | undefined,
  multipleUse: number | null | undefined,
): boolean {
  if (isUnlimited(multipleUse)) return false;
  return (usageCount ?? 0) >= (multipleUse ?? 0);
}

/** `3/∞` for an unlimited invite, `10/10` for a capped one. */
export function formatUsage(
  usageCount: number | null | undefined,
  multipleUse: number | null | undefined,
): string {
  const used = usageCount ?? 0;
  return isUnlimited(multipleUse) ? `${used}/∞` : `${used}/${multipleUse}`;
}

/**
 * D7: the roles a domain invite may grant — never owner, and never admin on a
 * project. Mirrors public.domain_invite_role_allowed (S1 migration
 * 20261010120000_cr185_domain_invite_capacity.sql), which enforces it.
 */
export function domainInviteRoleOptions(containerType: DomainInviteContainerType): WorkspaceRole[] {
  return containerType === "workspace" ? ["member", "admin"] : ["member", "contributor"];
}

const ROLES: readonly WorkspaceRole[] = ["owner", "admin", "member", "contributor"];

/**
 * The template row's role as the enum (container_members_invited types it as
 * plain text). An unexpected value reads as member, the safest label.
 */
export function asInviteRole(value: string | null | undefined): WorkspaceRole {
  return ROLES.find((role) => role === value) ?? "member";
}

/**
 * Whether a finite maximum can be saved: at least 1, at most the smallint
 * cap, and never below the people who already joined (the RPC refuses it
 * with 22023).
 */
export function isValidMaximum(maximum: number, usageCount: number): boolean {
  return (
    Number.isInteger(maximum) &&
    maximum >= 1 &&
    maximum <= MAX_DOMAIN_INVITE_USES &&
    maximum >= usageCount
  );
}

/** The `get_invite_by_code` fields that tell whether a domain link is full. */
export type InviteCapacityRow = {
  domain: string | null;
  multiple_use: number | null;
  usage_count: number | null;
  used: boolean | null;
};

/**
 * A domain invite that can take nobody else. `used` is set by apply_invite
 * when the last slot fills and cleared again when an owner raises the maximum,
 * so either signal counts.
 */
export function isFullDomainInvite(invite: InviteCapacityRow | null | undefined): boolean {
  if (!invite?.domain) return false;
  if (isUnlimited(invite.multiple_use)) return false;
  return invite.used === true || isFull(invite.usage_count, invite.multiple_use);
}

/** Error code apply_invite / accept_invite raise when a domain invite is full. */
export const DOMAIN_INVITE_FULL_ERROR_CODE = "P0185";

/**
 * CR-185 S5: what report_domain_invite_full answered. `already_member` = the
 * caller holds every row the invite grants (nobody was pinged; the slugs say
 * where to go, projectSlug null when the invite covers no single project).
 */
export type DomainInviteReportOutcome = DomainInviteReportRow["outcome"];

export type DomainInviteReport = {
  outcome: DomainInviteReportOutcome;
  workspaceSlug: string | null;
  projectSlug: string | null;
};

const REPORT_OUTCOMES: readonly DomainInviteReportOutcome[] = [
  "reported",
  "already_member",
  "not_full",
];

function isReportOutcome(value: unknown): value is DomainInviteReportOutcome {
  return REPORT_OUTCOMES.some((outcome) => outcome === value);
}

function slugOrNull(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

/**
 * The RPC returns a one-row table; anything unexpected reads as null — e.g.
 * the pre-S5 boolean answer while the new client meets the old function.
 */
export function parseDomainInviteReport(data: unknown): DomainInviteReport | null {
  if (!Array.isArray(data)) return null;
  const row: unknown = data[0];
  if (typeof row !== "object" || row === null) return null;
  const record = row as Record<string, unknown>;
  if (!isReportOutcome(record.outcome)) return null;
  return {
    outcome: record.outcome,
    workspaceSlug: slugOrNull(record.workspace_slug),
    projectSlug: slugOrNull(record.project_slug),
  };
}

export type DomainInviteUpdateErrorKind =
  | "not_found"
  | "below_usage"
  | "invalid"
  | "covers_unmanaged"
  | "forbidden"
  | "unknown";

export type DomainInviteCreateErrorKind = "role_ceiling" | "maximum_range";

/**
 * The create_container_invite refusals a domain invite adds (role ceiling,
 * maximum range), told apart by message: both are 22023 there, and the RPC's
 * older refusals carry no code at all. KEEP IN SYNC with the RAISE texts of
 * create_container_invite in 20261010120000_cr185_domain_invite_capacity.sql.
 */
export function classifyDomainInviteCreateError(
  error: unknown,
): DomainInviteCreateErrorKind | null {
  const message = error instanceof Error ? error.message : "";
  if (/Domain invites can grant/i.test(message)) return "role_ceiling";
  if (/maximum must be unlimited/i.test(message)) return "maximum_range";
  return null;
}

/**
 * Classifies an update_domain_invite refusal by its Postgres error code (DB messages are
 * not translated, so the UI maps them). 22023 = invalid maximum or role (the
 * "lower than the N members" case is told apart so the UI can name N);
 * 42501 = not allowed (a capacity change on an invite that also covers a
 * container the caller does not manage is told apart). KEEP IN SYNC with the
 * RAISE texts of update_domain_invite in
 * 20261010120000_cr185_domain_invite_capacity.sql.
 */
export function classifyDomainInviteUpdateError(error: {
  code?: string | null;
  message?: string | null;
}): DomainInviteUpdateErrorKind {
  const message = error.message ?? "";
  if (error.code === "22023") {
    if (/not found/i.test(message)) return "not_found";
    return /cannot be lower than/i.test(message) ? "below_usage" : "invalid";
  }
  if (error.code === "42501") {
    return /covers a container you do not manage/i.test(message) ? "covers_unmanaged" : "forbidden";
  }
  return "unknown";
}
